import { useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { Check, CheckCheck, Clock, Pin, VolumeX, PhoneIncoming, PhoneMissed, PhoneOutgoing, Video, Archive, Trash2, Volume2, ImageIcon, Mic, FileText, Star, UserCheck, MailOpen, ShieldOff, ShieldCheck, Heart, HeartOff, MessageSquareX, ArchiveX, LogOut } from "lucide-react"
import type { Conversation } from "./types"
import { useConversationStatus, useConversationHidden } from "@/features/presence/hooks/useConversationOnline"
import { statusDotClass } from "@/features/presence/utils/statusMeta"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/clientWeb/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/clientWeb/components/ui/dialog"
import { Button } from "@/clientWeb/components/ui/button"
import { Checkbox } from "@/clientWeb/components/ui/checkbox"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { AvatarPhoto } from "./avatar-photo"
import { useMessengerStore } from "./store/messenger.store"
import { usePointerMenu } from "./hooks/usePointerMenu"
import { PointerMenuAnchor } from "./hooks/PointerMenuAnchor"
import { useAuthStore } from "@/features/auth/store/auth.store"

interface ChatListTileProps {
  conversation: Conversation
  isSelected: boolean
  onClick: () => void
  onArchive?: (id: string) => void
  onPin?: (id: string) => void
  onMute?: (id: string) => void
  onDelete?: (id: string) => void
  /** Really leave a group, dropping my participant row. Group-only, and
   *  unlike `onDelete` this one asks for confirmation first: exiting can
   *  fail server-side, so a toast reports success or failure once it knows. */
  onExitGroup?: (id: string) => void
  onFavorite?: (id: string) => void
  onMarkAsRead?: (id: string) => void
  onViewContact?: (id: string) => void
  /**
   * Carries the confirm dialog's "Keep starred messages" choice, same as the
   * header and Contact Info versions of this action: the server needs it at
   * the moment of the clear, not read back afterward.
   */
  onClearChat?: (id: string, opts: { keepStarred: boolean }) => void
  onBlock?: (id: string) => void
  /**
   * Is this conversation's peer already blocked?
   *
   * The menu had no idea, so it offered "Block" over a contact that was
   * already blocked — and blocking again is a no-op, leaving the only way
   * back inside the thread or in settings.
   */
  isBlocked?: boolean
  onUnblock?: (id: string) => void
}

function Avatar({ name, imageUrl, isBot, isGroup, contactId, size = 48 }: { name: string; imageUrl?: string; isBot?: boolean; isGroup?: boolean; contactId?: string; size?: number }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
  const colors = ["bg-primary/20 text-primary", "bg-violet-500/20 text-violet-600", "bg-emerald-500/20 text-emerald-600", "bg-amber-500/20 text-amber-600", "bg-rose-500/20 text-rose-600", "bg-cyan-500/20 text-cyan-600"]
  const colorIdx = name.charCodeAt(0) % colors.length

  return (
    <div
      className={`relative flex items-center justify-center rounded-full overflow-hidden shrink-0 ${colors[colorIdx]}`}
      style={{ width: size, height: size }}
    >
      <AvatarPhoto
        src={imageUrl}
        alt={name}
        className="w-full h-full object-cover"
        fallback={<span className="text-sm font-semibold">{isBot ? "🤖" : isGroup ? "👥" : initials}</span>}
        contactId={contactId}
      />
    </div>
  )
}

function MessageStatusIcon({ status, className = "" }: { status: string; className?: string }) {
  switch (status) {
    case "sending":   return <Clock className={`w-3.5 h-3.5 text-muted-foreground ${className}`} />
    case "sent":      return <Check className={`w-3.5 h-3.5 text-muted-foreground ${className}`} />
    case "delivered": return <CheckCheck className={`w-3.5 h-3.5 text-muted-foreground ${className}`} />
    case "read":      return <CheckCheck className={`w-3.5 h-3.5 text-primary ${className}`} />
    default: return null
  }
}

export function ChatListTile({
  conversation: c, isSelected, onClick,
  onArchive, onPin, onMute, onDelete, onExitGroup,
  onFavorite, onMarkAsRead, onViewContact, onClearChat, onBlock, isBlocked, onUnblock,
}: ChatListTileProps) {
  const t = useTranslation()
  const [hovered, setHovered] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [confirmExit, setConfirmExit] = useState(false)
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(false)
  // A group I left: kept read-only, so Delete group replaces Exit group.
  const hasLeftGroup = useMessengerStore((s) => s.removedFromConversationIds.has(c.id))
  const [confirmClear, setConfirmClear] = useState(false)
  // Checked by default, like the header's dialog, and re-armed on every open
  // so an earlier uncheck doesn't quietly persist to the next clear.
  const [keepStarred, setKeepStarred] = useState(true)
  const isCallEntry = c.isCall || c.isMissedCall
  const myUserId = useAuthStore((s) => s.presenceId)
  // The other person in a DM, so the tile can ask the directory for their
  // CURRENT picture instead of trusting `c.imageUrl` (a copy taken when the
  // conversation was created). A group has no single peer, and a bot is not
  // a real directory contact, so neither gets a lookup.
  const peerId = !c.isGroup && !c.isBot ? c.participants?.find(p => p.id !== myUserId)?.id : undefined
  const peerStatus = useConversationStatus(c)
  const peerHidden = useConversationHidden(c)
  // No dot at all for a contact who hides their status, not even the
  // offline one.
  const dotClass = !c.isGroup && peerHidden ? undefined : statusDotClass[peerStatus]
  const pointerMenu = usePointerMenu()

  // Shared with the pointer menu below so a right-click opens the exact same
  // actions, dialogs and toasts as the three-dot button, not a second copy
  // of them that can drift out of sync.
  const menuItems = (
    <>
      {/* View contact */}
      <DropdownMenuItem onClick={() => onViewContact?.(c.id)}>
        <UserCheck className="w-4 h-4 mr-2" />
        {c.isGroup ? "View Group Info" : "View Contact"}
      </DropdownMenuItem>

      {/* Mark as read */}
      {c.unreadCount > 0 && (
        <DropdownMenuItem onClick={() => { onMarkAsRead?.(c.id); toast.info({ title: "Marked as read" }) }}>
          <MailOpen className="w-4 h-4 mr-2" />
          Mark as Read
        </DropdownMenuItem>
      )}

      <DropdownMenuSeparator />

      {/* Pin / Unpin */}
      <DropdownMenuItem onClick={() => {
        onPin?.(c.id)
        toast.info({ title: c.isPinned ? "Chat unpinned" : "Chat pinned", description: c.isPinned ? `${c.name} removed from pinned.` : `${c.name} pinned to top.` })
      }}>
        <Pin className="w-4 h-4 mr-2" style={{ transform: "rotate(45deg)" }} />
        {c.isPinned ? "Unpin" : "Pin"}
      </DropdownMenuItem>

      {/* Mute / Unmute */}
      <DropdownMenuItem onClick={() => {
        onMute?.(c.id)
        toast.info({ title: c.isMuted ? "Notifications unmuted" : "Notifications muted", description: c.isMuted ? `You'll receive notifications from ${c.name}.` : `Notifications from ${c.name} are muted.` })
      }}>
        {c.isMuted ? (
          <><Volume2 className="w-4 h-4 mr-2" />Unmute</>
        ) : (
          <><VolumeX className="w-4 h-4 mr-2" />Mute</>
        )}
      </DropdownMenuItem>

      {/* Archive / Unarchive */}
      <DropdownMenuItem onClick={() => {
        onArchive?.(c.id)
        toast.info({ title: c.isArchived ? "Chat unarchived" : "Chat archived", description: c.isArchived ? `${c.name} moved back to chats.` : `${c.name} moved to archive.` })
      }}>
        {c.isArchived ? (
          <><ArchiveX className="w-4 h-4 mr-2" />Unarchive</>
        ) : (
          <><Archive className="w-4 h-4 mr-2" />Archive</>
        )}
      </DropdownMenuItem>

      {/* Favourite */}
      <DropdownMenuItem onClick={() => {
        onFavorite?.(c.id)
        toast.success({ title: c.isFavorite ? "Removed from favourites" : "Added to favourites", description: c.isFavorite ? `${c.name} removed from your favourites.` : `${c.name} added to your favourites.` })
      }}>
        {c.isFavorite ? (
          <><HeartOff className="w-4 h-4 mr-2" />Remove from Favourites</>
        ) : (
          <><Heart className="w-4 h-4 mr-2" />Add to Favourites</>
        )}
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      {/* Clear chat: confirms first, same as the header and Contact Info
          versions of this action, instead of firing on click like the rest
          of this menu. */}
      {!hasLeftGroup && (
      <DropdownMenuItem onClick={() => { setKeepStarred(true); setConfirmClear(true) }}>
        <MessageSquareX className="w-4 h-4 mr-2" />
        Clear Chat
      </DropdownMenuItem>
      )}

      {/* Block / Unblock: whichever the contact is not already */}
      {!c.isGroup && (
        isBlocked ? (
          <DropdownMenuItem onClick={() => {
            onUnblock?.(c.id)
            toast.success({ title: "Contact unblocked", description: `${c.name} has been unblocked.` })
          }}>
            <ShieldCheck className="w-4 h-4 mr-2" />
            Unblock
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={() => {
            onBlock?.(c.id)
            toast.warning({ title: "Contact blocked", description: `${c.name} has been blocked.` })
          }}>
            <ShieldOff className="w-4 h-4 mr-2" />
            Block
          </DropdownMenuItem>
        )
      )}

      <DropdownMenuSeparator />

      {/* Delete hides the chat for me. A member cannot delete a
          group: hiding it would leave them in it, invisibly, until
          someone posts. Groups get Clear chat and Exit group. */}
      {!c.isGroup && (
        <DropdownMenuItem onClick={() => {
          onDelete?.(c.id)
          // "Shoyab has been removed" reads as the person being
          // removed. What was removed is the conversation; the
          // name only says which one.
          toast.success({
            title: t('presence.chatDeleted'),
            description: t('presence.chatDeletedWith', { name: c.name }),
          })
        }} variant="destructive">
          <Trash2 className="w-4 h-4 mr-2" />
          {"Delete Chat"}
        </DropdownMenuItem>
      )}

      {/* The real leave. Confirms first, because exiting is the one
          action on this menu that reports its own success or
          failure back once the request resolves. */}
      {c.isGroup && !hasLeftGroup && (
        <DropdownMenuItem
          onClick={() => setConfirmExit(true)}
          variant="destructive"
        >
          <LogOut className="w-4 h-4 mr-2" />
          {t("presence.exitGroupAction")}
        </DropdownMenuItem>
      )}
      {/* Already left: removes the group from MY chats; it carries on for
          everyone else. */}
      {c.isGroup && hasLeftGroup && (
        <DropdownMenuItem
          onClick={() => setConfirmDeleteGroup(true)}
          variant="destructive"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          {t("presence.deleteGroupAction")}
        </DropdownMenuItem>
      )}
    </>
  )

  return (
    <>
    <div
      role="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onContextMenu={pointerMenu.onContextMenu}
      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors select-none
        ${isSelected ? "bg-primary/8 dark:bg-primary/12" : hovered ? "bg-muted/50" : "bg-transparent"}`}
    >
      {/* Avatar */}
      <div className="relative shrink-0">
        <Avatar name={c.name} imageUrl={c.imageUrl} isBot={c.isBot} isGroup={c.isGroup} contactId={peerId} size={46} />
        {dotClass && (
          <span className={`absolute bottom-0.5 right-0.5 w-3 h-3 rounded-full ${dotClass} border-2 border-background`} />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Name + time row */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0">
            {c.isPinned && <Pin className="w-3 h-3 text-muted-foreground shrink-0" style={{ transform: "rotate(45deg)" }} />}
            {c.isFavorite && <Star className="w-3 h-3 text-amber-400 fill-amber-400 shrink-0" />}
            <span
              className={`text-sm truncate ${c.unreadCount > 0 ? "font-semibold text-foreground" : "font-medium text-foreground"}`}
              title={c.name}
            >
              {c.name}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <span className={`text-[11px] ${c.unreadCount > 0 ? "text-primary font-medium" : "text-muted-foreground"}`}>
              {c.time}
            </span>
            {(hovered || menuOpen) && (
              <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    className="w-5 h-5 rounded-full flex items-center justify-center bg-muted-foreground/20 hover:bg-muted-foreground/30 transition-colors"
                    onClick={e => { e.stopPropagation(); setMenuOpen(true) }}
                  >
                    <svg className="w-3 h-3 text-muted-foreground" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4z" />
                    </svg>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52" onClick={e => e.stopPropagation()}>
                  {menuItems}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {/* Right-click / two-finger-click menu, positioned at the pointer.
                A separate, controlled DropdownMenu rather than the one above:
                Radix positions content against its own trigger, and the real
                three-dot button isn't where the click happened. */}
            {pointerMenu.point && (
              <DropdownMenu open={pointerMenu.open} onOpenChange={pointerMenu.setOpen}>
                <DropdownMenuTrigger asChild>
                  <PointerMenuAnchor point={pointerMenu.point} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52" onClick={e => e.stopPropagation()}>
                  {menuItems}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Last message row */}
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <div className="flex items-center gap-1 min-w-0">
            {c.isTyping ? (
              <div className="flex items-center gap-0.5">
                {[0, 1, 2].map(i => (
                  <span key={i} className="w-1 h-1 rounded-full bg-primary animate-bounce" style={{ animationDelay: `${i * 100}ms` }} />
                ))}
                <span className="text-xs text-primary ml-1 font-medium">
                  {c.isGroup && c.typingUser ? `${c.typingUser} typing…` : "typing…"}
                </span>
              </div>
            ) : (
              <>
                {c.isSentByMe && !isCallEntry && <MessageStatusIcon status={c.messageStatus} className="shrink-0" />}
                {isCallEntry && (
                  c.isMissedCall
                    ? <PhoneMissed className="w-3.5 h-3.5 text-destructive shrink-0" />
                    : c.callDir === "out"
                      ? <PhoneOutgoing className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      : c.isVideoCall
                        ? <Video className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        : <PhoneIncoming className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                )}
                {!isCallEntry && c.lastMessageType === "image" && <ImageIcon className="w-3 h-3 text-muted-foreground/70 shrink-0" />}
                {!isCallEntry && c.lastMessageType === "audio" && <Mic className="w-3 h-3 text-muted-foreground/70 shrink-0" />}
                {!isCallEntry && c.lastMessageType === "file"  && <FileText className="w-3 h-3 text-muted-foreground/70 shrink-0" />}
                <span className={`text-xs truncate ${c.isMissedCall ? "text-destructive font-medium" : c.unreadCount > 0 ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                  {c.lastMessageType === "image" ? "Photo" : c.lastMessageType === "audio" ? "Voice message" : c.lastMessageType === "file" ? "Document" : c.lastMessage}
                </span>
              </>
            )}
          </div>

          {/* Badges */}
          <div className="flex items-center gap-1 shrink-0">
            {c.isMuted && <VolumeX className="w-3.5 h-3.5 text-muted-foreground/50" />}
            {c.isPinned && !c.unreadCount && <Pin className="w-3 h-3 text-muted-foreground/40" style={{ transform: "rotate(45deg)" }} />}
            {c.unreadCount > 0 && (
              <span className="min-w-[18px] px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground bg-primary rounded-full text-center leading-none">
                {c.unreadCount > 99 ? "99+" : c.unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>

    <Dialog open={confirmDeleteGroup} onOpenChange={setConfirmDeleteGroup}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("presence.deleteGroup")}</DialogTitle>
          <DialogDescription>
            {t("presence.deleteGroupDescWithName", { name: c.name })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => setConfirmDeleteGroup(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              onDelete?.(c.id)
              setConfirmDeleteGroup(false)
              toast.success({ title: t("presence.chatDeleted"), description: t("presence.chatDeletedWith", { name: c.name }) })
            }}
          >
            {t("common.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Exit-group confirmation: the one destructive action on this menu that
        waits for the server before the row disappears, so it gets a dialog
        of its own instead of firing on click like the others here. */}
    <Dialog open={confirmExit} onOpenChange={setConfirmExit}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("presence.exitGroup")}</DialogTitle>
          <DialogDescription>
            {t("presence.exitGroupDescWithName", { name: c.name })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => setConfirmExit(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={() => { onExitGroup?.(c.id); setConfirmExit(false) }}
          >
            {t("common.exit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Clear-chat confirmation, matching the header and Contact Info dialogs:
        same title/description, same "keep starred" checkbox checked by
        default and re-armed on every open. */}
    <Dialog
      open={confirmClear}
      onOpenChange={open => {
        setConfirmClear(open)
        if (open) setKeepStarred(true)
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("presence.clearChat")}</DialogTitle>
          <DialogDescription>{t("presence.clearChatDesc")}</DialogDescription>
        </DialogHeader>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <Checkbox
            checked={keepStarred}
            onCheckedChange={(v) => setKeepStarred(v === true)}
          />
          {t("presence.keepStarredMessages")}
        </label>
        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => setConfirmClear(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              onClearChat?.(c.id, { keepStarred })
              toast.success({ title: t("presence.chatCleared"), description: t("presence.chatClearedDesc") })
              setConfirmClear(false)
            }}
          >
            {t("common.clear")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}
